import { themeVar } from '@/styles/theme';
import styled from 'styled-components';

import { FatSeekingInterestPageInvestmentsByWorkflowStateQuery } from '@/__generated__/graphql';
import client from '@/apollo-client';
import Button from '@/components/basicComponents/button';
import { formatCash } from '@/shared/utils/currency';
import { useMutation } from '@apollo/client';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useResponsive } from '../../../hooks/use-responsive';
import { CREATE_INVESTOR_INTERESTS_MUTATION, DELETE_INVESTOR_INTEREST_AMOUNT_MUTATION } from '../queries';
import { INVESTMENTS_BY_WORKFLOW_STATE_MIN_QUERY } from './queries';
import { IInvestmentEntity } from './types';

interface FooterModalProps {
  selectedRows: IInvestmentEntity[];
  handleClosePage: (withRefetch?: boolean) => void;
}

const FooterModal = ({ selectedRows, handleClosePage }: FooterModalProps) => {
  const { isMobile, isTablet } = useResponsive();
  const params = useParams();

  const totalValue = useMemo(() => {
    return selectedRows.reduce((accumulator, currentValue) => accumulator + currentValue.investor_interest[0].amount, 0);
  }, [selectedRows]);

  const [createInvestorInterest] = useMutation(CREATE_INVESTOR_INTERESTS_MUTATION);
  const [deleteInvestorInterest] = useMutation(DELETE_INVESTOR_INTEREST_AMOUNT_MUTATION);

  const deleteAmount = async (ids: string[]) => {
    deleteInvestorInterest({
      variables: {
        ids
      }
    });
  };

  const saveEstimate = async () => {
    const { data: investorInterestsData } = await client.query({
      query: INVESTMENTS_BY_WORKFLOW_STATE_MIN_QUERY,
      fetchPolicy: 'network-only',
      variables: {
        data: {
          limit: 1000,
          assetClassFilter: [],
          workflowStatesFilter: ['seekingInterest']
        }
      }
    });
    const currentInvestorInterests = investorInterestsData.InvestmentsByWorkflowState.data.find(
      (item: FatSeekingInterestPageInvestmentsByWorkflowStateQuery['InvestmentsByWorkflowState']['data'][number]) => item.investment.id === params?.id
    );

    const ids = currentInvestorInterests?.investorInterests.map(
      (item: FatSeekingInterestPageInvestmentsByWorkflowStateQuery['InvestmentsByWorkflowState']['data'][number]['investorInterests'][number]) =>
        item.id
    );

    const createDataToUpdate = selectedRows.map((row) => {
      return {
        amount: row.investor_interest[0].amount,
        investmentId: params?.id,
        legalEntityId: row.id,
        exempt: row.taxExempt
      };
    });

    if (ids?.length) {
      await deleteAmount(ids);
    }

    await createInvestorInterest({
      variables: {
        data: createDataToUpdate
      },
      onCompleted: () => {
        handleClosePage(true);
      }
    });
  };

  return (
    <FooterModalWrap isMobile={isMobile} isTablet={isTablet}>
      <BtnWrap>
        <Button
          size="lg"
          onClick={saveEstimate}
          styleType={!selectedRows.length ? 'disabled' : 'default'}
          icon={
            <BackWrap disabled={!selectedRows.length}>
              <EditCount disabled={false}>{selectedRows.length}</EditCount>
            </BackWrap>
          }
        >
          Save Estimate
        </Button>
        <EditDesc>
          <BoldInfo>{formatCash(totalValue)}</BoldInfo> total value
        </EditDesc>
      </BtnWrap>
    </FooterModalWrap>
  );
};

const EditDesc = styled.div`
  font-family: Blinker, serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0;
  text-align: left;
  color: ${themeVar('font.base')};
`;

const BtnWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  @media (max-width: 990px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const Count = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 700;
  font-size: 13px;
`;

const EditCount = styled(Count)<{ disabled: boolean }>`
  color: ${({ disabled }) => (disabled ? themeVar('font.disabled') : themeVar('font.strong'))};
`;

const BackWrap = styled.div<{ disabled: boolean }>`
  background-color: ${({ disabled }) => (disabled ? themeVar('layer.2') : themeVar('layer.1'))};
  line-height: 15px;
  display: inline-block;
  border-radius: 12px;
  color: ${themeVar('context.light')};
  padding: 2px 12px;
  font-size: 13px;
  margin: 0 5px;
`;

const FooterModalWrap = styled.div<{ isMobile: boolean; isTablet: boolean }>`
  width: ${({ isMobile, isTablet }) => (isMobile || isTablet ? '100%' : 'calc(100% - 80px)')};
  display: flex;
  align-items: center;
  padding: 20px 80px;
  position: fixed;
  bottom: 0;
  clear: both;
  background-color: ${themeVar('layer.1')};
  border-top: 1px solid ${themeVar('border.base')};
  max-height: 90px;
  height: 100%;
  z-index: 30;
`;

const BoldInfo = styled.div`
  display: inline-block;
  font-weight: 700;
  font-size: 16px;
  color: ${themeVar('font.base')};
`;

export default FooterModal;
